/**
 * Credit Purchase Modal Component
 * Allows users to purchase credits with variable amounts using Paystack.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import {
  CreditCard,
  Shield,
  Sparkles,
  AlertCircle,
  TrendingUp,
  X
} from 'lucide-react';
import { usePostHog } from '@posthog/react';
import { useAuth } from '../../context/AuthContext';
import {
  useCreditCalculator,
  formatCurrency,
  formatSavings,
  CREDIT_PACKS
} from '../../hooks/useCreditCalculator';

export default function CreditPurchaseModal({ isOpen, onClose, onPaymentSuccess }) {
  const { currentUser, refreshUserData } = useAuth();
  const posthog = usePostHog();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const {
    quantity,
    setQuantity,
    pricingDetails,
    validation,
    selectQuickAmount,
    getNextTierSuggestion,
    config
  } = useCreditCalculator();

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setError(null);
      setIsProcessing(false);
    }
  }, [isOpen]);

  // Handle Paystack payment
  const handlePaystackPayment = useCallback(async () => {
    if (!validation.canProceed || isProcessing) return;

    setIsProcessing(true);
    setError(null);

    posthog?.capture('credit_purchase_initiated', {
      credits: pricingDetails.quantity,
      total_price_zar: pricingDetails.totalPrice,
      price_per_credit: pricingDetails.pricePerCredit,
      tier: pricingDetails.tier,
      savings_percentage: pricingDetails.savingsPercentage,
    });

    try {
      // Get Paystack public key from environment
      const paystackPublicKey = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY;

      if (!paystackPublicKey) {
        throw new Error('Payment configuration not available');
      }

      // Import Paystack library dynamically
      const PaystackPop = (await import('@paystack/inline-js')).default;
      const paystack = new PaystackPop();

      // Generate unique reference
      const reference = `credits_${currentUser.uid.substring(0, 8)}_${Date.now()}`;

      const paymentConfig = {
        key: paystackPublicKey,
        email: currentUser.email,
        amount: Math.round(pricingDetails.totalPrice * 100), // Convert to cents
        currency: 'ZAR',
        ref: reference,
        metadata: {
          userId: currentUser.uid,
          credits: pricingDetails.quantity,
          packageId: `variable_${pricingDetails.quantity}`,
          userEmail: currentUser.email
        },
        callback: async function(response) {
          if (posthog) {
            posthog.capture('credit_purchase_completed', {
              reference: response.reference,
              credits: pricingDetails.quantity,
              total_price_zar: pricingDetails.totalPrice,
              tier: pricingDetails.tier,
            });
          }

          // Refresh user data to get updated credits
          await refreshUserData();

          // Call success callback
          if (onPaymentSuccess) {
            onPaymentSuccess({
              reference: response.reference,
              credits: pricingDetails.quantity,
              amount: pricingDetails.totalPrice
            });
          }

          setIsProcessing(false);
          onClose();
        },
        onClose: function() {
          setIsProcessing(false);
        }
      };

      paystack.newTransaction(paymentConfig);

    } catch (err) {
      console.error('Payment error:', err);
      posthog?.captureException(err);
      setError(err.message || 'Payment failed. Please try again.');
      setIsProcessing(false);
    }
  }, [currentUser, pricingDetails, validation.canProceed, isProcessing, onPaymentSuccess, onClose, refreshUserData, posthog]);

  const nextTierSuggestion = getNextTierSuggestion();

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isProcessing && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle>Purchase Credits</DialogTitle>
              <DialogDescription>
                Buy credits to process your documents
              </DialogDescription>
            </div>
            <button
              onClick={() => !isProcessing && onClose()}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              disabled={isProcessing}
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Quick Select Packs */}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 block">
              Quick Select
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {CREDIT_PACKS.map((pack) => (
                <button
                  key={pack.id}
                  onClick={() => selectQuickAmount(pack.credits)}
                  disabled={isProcessing}
                  className={`
                    relative p-3 rounded-lg border-2 transition-all text-left
                    ${quantity === pack.credits
                      ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                    }
                    ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                >
                  {pack.popular && (
                    <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-emerald-500 text-white text-xs font-medium rounded-full">
                      Popular
                    </span>
                  )}
                  <div className="text-lg font-bold text-gray-900 dark:text-white">
                    {pack.credits}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    R{pack.price}
                  </div>
                  {pack.savings > 0 && (
                    <div className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                      Save {pack.savings}%
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Amount Slider */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Custom Amount
              </label>
              <span className="text-lg font-bold text-gray-900 dark:text-white">
                {quantity} credits
              </span>
            </div>

            <input
              type="range"
              min={config.MINIMUM_PURCHASE}
              max={config.MAXIMUM_PURCHASE}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              disabled={isProcessing}
              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-500"
            />

            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>{config.MINIMUM_PURCHASE}</span>
              <span>{config.MAXIMUM_PURCHASE}</span>
            </div>
          </div>

          {/* Pricing Summary */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Price per credit:</span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {formatCurrency(pricingDetails.pricePerCredit)}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Total credits:</span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {pricingDetails.quantity}
              </span>
            </div>

            {pricingDetails.savings > 0 && (
              <div className="flex items-center justify-between text-emerald-600 dark:text-emerald-400">
                <div className="flex items-center gap-1">
                  <Sparkles className="w-4 h-4" />
                  <span className="text-sm">You save:</span>
                </div>
                <span className="font-semibold">
                  {formatSavings(pricingDetails.savings, pricingDetails.savingsPercentage)}
                </span>
              </div>
            )}

            <div className="border-t border-gray-200 dark:border-gray-700 pt-3 flex items-center justify-between">
              <span className="font-semibold text-gray-900 dark:text-white">Total:</span>
              <span className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                {formatCurrency(pricingDetails.totalPrice)}
              </span>
            </div>
          </div>

          {/* Validation Errors */}
          {validation.errors.length > 0 && (
            <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
              <div className="text-sm text-red-700 dark:text-red-300">
                {validation.errors.map((err, i) => (
                  <div key={i}>{err}</div>
                ))}
              </div>
            </div>
          )}

          {/* Next Tier Suggestion */}
          {nextTierSuggestion && (
            <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <TrendingUp className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-medium text-blue-800 dark:text-blue-300">
                  Better value available!
                </div>
                <div className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                  Get {nextTierSuggestion.credits} credits for {nextTierSuggestion.tier} pricing - {nextTierSuggestion.savings}
                </div>
                <button
                  onClick={() => selectQuickAmount(nextTierSuggestion.credits)}
                  disabled={isProcessing}
                  className="mt-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 disabled:opacity-50"
                >
                  Select {nextTierSuggestion.credits} credits →
                </button>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
              <div className="text-sm text-red-700 dark:text-red-300">{error}</div>
            </div>
          )}

          {/* Payment Button */}
          <Button
            onClick={handlePaystackPayment}
            disabled={!validation.canProceed || isProcessing}
            className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white"
            size="lg"
          >
            {isProcessing ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Processing...
              </div>
            ) : (
              <div className="flex items-center justify-center">
                <CreditCard className="w-5 h-5 mr-2" />
                Pay {formatCurrency(pricingDetails.totalPrice)} with Paystack
              </div>
            )}
          </Button>

          {/* Security Notice */}
          <div className="flex items-center justify-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <Shield className="w-4 h-4" />
            Secure payment powered by Paystack
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
